import { useEffect, useRef } from 'react';
import { useLocation, history } from 'umi';
import { getBasePath } from '@/utils/utils';
import { filterGameIdsByEntityType } from '@intelligence/v3/utils/topcharts';

import type { IGameType } from '@/pages/intelligence/types/common';

interface IUseUrlGamesParamOptions {
  entityType: IGameType | 'pc,console';
  existingGameIds: string[];
  addComparingGames: (games: any[]) => Promise<any>;
  onGamesAdded?: (games: any[]) => void;
}

/**
 * Hook to handle URL games parameter for Compare pages
 * Parses ?games=id1,id2,... from URL and auto-adds those games to comparison
 */
const useUrlGamesParam = (options: IUseUrlGamesParamOptions): void => {
  const { entityType, existingGameIds, addComparingGames, onGamesAdded } = options;
  const location = useLocation();
  const processedRef = useRef(false);

  useEffect(() => {
    console.log('[useUrlGamesParam] Effect triggered', {
      processed: processedRef.current,
      search: location.search,
      existingGameIds,
      hasAddFn: typeof addComparingGames === 'function',
      hasCallbackFn: typeof onGamesAdded === 'function',
    });

    // Only process once per page load
    if (processedRef.current) {
      console.log('[useUrlGamesParam] Already processed, skipping');
      return;
    }

    // Wait for required functions to be available
    if (typeof addComparingGames !== 'function') {
      console.log('[useUrlGamesParam] addComparingGames not ready');
      return;
    }
    if (typeof onGamesAdded !== 'function') {
      console.log('[useUrlGamesParam] onGamesAdded not ready');
      return;
    }

    const searchParams = new URLSearchParams(location.search);
    const gamesParam = searchParams.get('games');

    console.log('[useUrlGamesParam] gamesParam:', gamesParam);

    if (!gamesParam) {
      console.log('[useUrlGamesParam] No games param in URL');
      return;
    }

    const gameIds = gamesParam
      .split(',')
      .map((id) => id.trim())
      .filter((id) => id.length > 0);

    const filteredGameIds = filterGameIdsByEntityType(entityType, gameIds);

    if (filteredGameIds.length === 0) {
      console.log('[useUrlGamesParam] No valid game IDs');
      return;
    }

    // Filter out games that are already in the comparison list
    const newGameIds = filteredGameIds.filter((id) => !existingGameIds.includes(id));

    console.log('[useUrlGamesParam] newGameIds:', newGameIds);

    if (newGameIds.length === 0) {
      // All games already exist, just clear the URL param
      console.log('[useUrlGamesParam] All games already exist');
      clearUrlGamesParam();
      processedRef.current = true;
      return;
    }

    // Mark as processed before starting async operation
    processedRef.current = true;

    // Add games to comparison with minimal info
    // The compare table API will fetch full game details
    const addGamesFromUrl = async (): Promise<void> => {
      try {
        // For PC/Console unified games, use 'combine' as entity type
        // For mobile games, use 'mobile'
        const gameEntityType = entityType === 'pc,console' ? 'combine' : entityType;

        console.log('[useUrlGamesParam] Adding games with entityType:', gameEntityType);

        // Create minimal game objects - the compare API will fetch full details
        const gamesToAdd = newGameIds.map((gameId) => ({
          entityType: gameEntityType,
          gameId: gameId,
          gameName: '', // Will be populated by compare table API
          cover: '',
          pcCover: '',
          consoleCover: '',
          publisher: '',
          releaseTime: '',
          pcReleaseTime: '',
          consoleReleaseTime: '',
        }));

        console.log('[useUrlGamesParam] gamesToAdd:', gamesToAdd);

        if (gamesToAdd.length > 0) {
          console.log('[useUrlGamesParam] Calling addComparingGames...');
          await addComparingGames(gamesToAdd);
          console.log('[useUrlGamesParam] addComparingGames done, calling onGamesAdded...');
          // Pass the added games to the callback so it can update the UI
          onGamesAdded(gamesToAdd);
          console.log('[useUrlGamesParam] onGamesAdded done');
        }
      } catch (error) {
        console.error('[useUrlGamesParam] Failed to add games from URL:', error);
      } finally {
        // Clear the games param from URL after processing
        console.log('[useUrlGamesParam] Clearing URL param');
        clearUrlGamesParam();
      }
    };

    addGamesFromUrl();
  }, [location.search, existingGameIds, entityType, addComparingGames, onGamesAdded]);
};

/**
 * Clear the games parameter from URL without triggering navigation
 */
const clearUrlGamesParam = (): void => {
  const url = new URL(window.location.href);
  url.searchParams.delete('games');
  // Remove base path (/v2 or /rebuildv2) before calling history.replace
  // because Umi router will add it back automatically
  const basePath = getBasePath();
  let pathname = url.pathname;
  if (basePath && pathname.startsWith(basePath)) {
    pathname = pathname.slice(basePath.length) || '/';
  }
  history.replace(pathname + url.search);
};

export default useUrlGamesParam;
